import { useState } from "react";
import { Download, CheckCircle2, FileText, Lock, Loader2 } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { Reveal } from "@/components/motion/Reveal";
import { useToast } from "@/hooks/use-toast";
import { submitLeadMagnetDownload } from "@/lib/leadMagnet.functions";
import coverAsset from "@/assets/lead-magnet/7-secrets-cover.png.asset.json";
import pdfAsset from "@/assets/lead-magnet/CTR7SecretsGuide.pdf.asset.json";

const bullets = [
  "How to legally reduce what you owe the IRS",
  "Your rights when you owe $10,000 or more",
  "How to stop wage garnishments — fast",
  "Settlement options the IRS never advertises",
];

interface Props {
  /** "section" = full-bleed page section. "card" = embeddable card (e.g. sidebar). */
  variant?: "section" | "card";
}

function getUtm() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  return {
    utmSource: p.get("utm_source") || undefined,
    utmMedium: p.get("utm_medium") || undefined,
    utmCampaign: p.get("utm_campaign") || undefined,
  };
}

function triggerDownload() {
  if (typeof window === "undefined") return;
  const a = document.createElement("a");
  a.href = pdfAsset.url;
  a.download = "CTR-7-Secrets-Guide.pdf";
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function useLeadForm() {
  const { toast } = useToast();
  const submit = useServerFn(submitLeadMagnetDownload);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName || !trimmedEmail) {
      toast({ title: "Please enter your name and email" });
      return;
    }
    setBusy(true);
    try {
      await submit({
        data: {
          name: trimmedName,
          email: trimmedEmail,
          guideSlug: "7-secrets",
          sourcePath:
            typeof window !== "undefined" ? window.location.pathname : undefined,
          userAgent:
            typeof navigator !== "undefined" ? navigator.userAgent : undefined,
          ...getUtm(),
        },
      });
      setDone(true);
      triggerDownload();
      toast({
        title: "Your guide is downloading",
        description: "Check your downloads folder.",
      });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: (err as Error)?.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return { name, setName, email, setEmail, busy, done, onSubmit };
}

export const LeadMagnetCTA = ({ variant = "section" }: Props) => {
  const form = useLeadForm();

  const Cover = (
    <div className="relative mx-auto w-full max-w-[320px]">
      <div className="absolute inset-0 -rotate-2 rounded-md bg-gold/30 blur-md" aria-hidden />
      <div className="absolute -left-2 top-2 h-full w-full -rotate-3 rounded-md bg-white/40" aria-hidden />
      <div className="relative block overflow-hidden rounded-md shadow-2xl ring-1 ring-black/10">
        <img
          src={coverAsset.url}
          alt="Cover of the free guide: 7 Things the IRS Does Not Want You to Know About — by Comprehensive Tax Resolution"
          width={620}
          height={802}
          loading="lazy"
          className="block w-full h-auto"
        />
        <span className="pointer-events-none absolute inset-y-0 left-0 w-2 bg-gradient-to-r from-black/30 to-transparent" />
      </div>
      <span className="absolute -top-3 -right-3 rounded-full bg-gold px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-navy-dark shadow-lg">
        Free PDF
      </span>
    </div>
  );

  const LeadForm = (
    <form onSubmit={form.onSubmit} className="mt-6 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
      <label className="sr-only" htmlFor="lm-name">Your name</label>
      <input
        id="lm-name"
        type="text"
        autoComplete="name"
        placeholder="Your name"
        value={form.name}
        onChange={(e) => form.setName(e.target.value)}
        required
        maxLength={120}
        className="rounded-md bg-white/95 px-4 py-3 text-sm text-navy-dark placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold"
      />
      <label className="sr-only" htmlFor="lm-email">Email address</label>
      <input
        id="lm-email"
        type="email"
        autoComplete="email"
        placeholder="Email address"
        value={form.email}
        onChange={(e) => form.setEmail(e.target.value)}
        required
        maxLength={255}
        className="rounded-md bg-white/95 px-4 py-3 text-sm text-navy-dark placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold"
      />
      <button
        type="submit"
        disabled={form.busy}
        className="inline-flex items-center justify-center gap-2 rounded-md bg-gold px-6 py-3 text-sm font-bold uppercase tracking-wider text-navy-dark shadow-lg transition-transform hover:scale-[1.02] hover:bg-gold-light disabled:opacity-70"
      >
        {form.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        {form.done ? "Sent" : "Get the guide"}
      </button>
    </form>
  );

  const Content = (
    <div className="text-white">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-gold">
        <FileText className="h-3 w-3" /> Free download • Instant PDF
      </span>
      <h2 className="mt-4 font-heading text-3xl lg:text-5xl font-bold leading-[1.1] [text-wrap:balance]">
        7 Things the IRS Does <span className="text-gold">Not Want You to Know.</span>
      </h2>
      <p className="mt-4 max-w-xl text-base lg:text-lg text-white/80 leading-relaxed">
        Insider tax secrets from our CPA team — settlement strategies, taxpayer
        rights, and the fastest ways to stop IRS collections. Enter your name and
        email and we'll start your download.
      </p>
      <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 text-sm text-white/85">
            <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-gold" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      {LeadForm}
      <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-white/60">
        <Lock className="h-3.5 w-3.5" /> We respect your privacy. Used only to send you tax-help resources.
      </p>
    </div>
  );

  if (variant === "card") {
    return (
      <aside className="rounded-xl bg-gradient-navy p-6 shadow-xl">
        <div className="mb-5">{Cover}</div>
        <div className="text-white">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gold">
            Free guide
          </p>
          <h3 className="mt-2 font-heading text-xl font-bold leading-snug">
            7 Things the IRS Does Not Want You to Know
          </h3>
          <p className="mt-2 text-sm text-white/75 leading-relaxed">
            Settlement strategies and taxpayer rights, written by our CPA team.
          </p>
          <form onSubmit={form.onSubmit} className="mt-4 grid gap-2">
            <input
              type="text"
              autoComplete="name"
              placeholder="Your name"
              value={form.name}
              onChange={(e) => form.setName(e.target.value)}
              required
              maxLength={120}
              aria-label="Your name"
              className="w-full rounded-md bg-white/95 px-3 py-2.5 text-sm text-navy-dark placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold"
            />
            <input
              type="email"
              autoComplete="email"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => form.setEmail(e.target.value)}
              required
              maxLength={255}
              aria-label="Email address"
              className="w-full rounded-md bg-white/95 px-3 py-2.5 text-sm text-navy-dark placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold"
            />
            <button
              type="submit"
              disabled={form.busy}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-gold px-4 py-3 text-xs font-bold uppercase tracking-wider text-navy-dark hover:bg-gold-light disabled:opacity-70"
            >
              {form.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {form.done ? "Downloaded" : "Download free PDF"}
            </button>
          </form>
        </div>
      </aside>
    );
  }

  return (
    <section className="relative overflow-hidden bg-gradient-navy py-16 lg:py-24">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        aria-hidden
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, hsl(var(--gold)) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      <div className="container relative mx-auto px-4 max-w-6xl">
        <Reveal>
          <div className="grid items-center gap-10 lg:gap-16 lg:grid-cols-[auto_1fr]">
            <div className="lg:pr-4">{Cover}</div>
            {Content}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default LeadMagnetCTA;
